import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, AlertCircle, AlertTriangle, Loader2, Edit2, Eye, EyeOff, Plus, Trash2, Sparkles, FileSpreadsheet, FileText, Upload, Search, Code, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import AttributeRow from './AttributeRow';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { useControlSpec } from '../contexts/ControlSpecContext';
import { frequencyOptions } from '../utils/controlConstants';

// Icon mapping for streaming status events
const STATUS_ICONS = {
    'upload': Upload,
    'spreadsheet': FileSpreadsheet,
    'file-text': FileText,
    'file': FileText,
    'search': Search,
    'code': Code,
    'check': CheckCircle,
    'sparkles': Sparkles,
    'loader': Loader2,
    'alert': AlertCircle,
    'image': FileText
};

// Auto-resize textarea component
const AutoResizeTextarea = ({ value, onChange, className, placeholder, readOnly = false }) => {
    const textareaRef = useRef(null);

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, []);

    useEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);

    return (
        <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
                if (!readOnly) {
                    onChange(e);
                    adjustHeight();
                }
            }}
            placeholder={placeholder}
            readOnly={readOnly}
            className={`${className} overflow-hidden resize-none ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            rows={1}
        />
    );
};

// Session storage keys for caching
const EXTRACTION_CACHE_KEY = 'skeptics_extraction_cache_v2';
const CONTROL_SPEC_CACHE_KEY = 'skeptics_control_spec_cache';

// Stage enum
const STAGE = {
    EXTRACTING: 'extracting',
    CONFIRM_EXTRACTION: 'confirm_extraction',
    GENERATING_STEPS: 'generating_steps',
    REVIEW_STEPS: 'review_steps'
};

const TestStepGeneration = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { initializeControlSpec } = useControlSpec();

    // Get data from navigation state
    // controlSpec may be passed when navigating back from DataSourceConfiguration
    // draftControlId is passed when activating a draft control from Controls list
    // extractedInfo is passed from unified upload when AI already extracted control info
    const { fileId, userHash, file, controlSpec: stateControlSpec, draftControlId, extractedInfo: stateExtractedInfo } = location.state || {};

    // Stage management
    const [stage, setStage] = useState(STAGE.EXTRACTING);

    // Extraction state (Stage 1)
    const [extractedInfo, setExtractedInfo] = useState(null);
    const [existingTestSteps, setExistingTestSteps] = useState(null); // SCRUM-268: test steps from uploaded file
    const [editableControlId, setEditableControlId] = useState('');
    const [editableTitle, setEditableTitle] = useState('');
    const [editableFrequency, setEditableFrequency] = useState('');
    const [editableWording, setEditableWording] = useState('');
    const [editableObjective, setEditableObjective] = useState('');

    // New mandatory fields
    const [editableSoxProcess, setEditableSoxProcess] = useState('');
    const [editableControlPerformer, setEditableControlPerformer] = useState('');
    const [editableSignificance, setEditableSignificance] = useState('');

    // Track if user attempted to proceed with missing fields
    const [showFieldHints, setShowFieldHints] = useState(false);

    // frequencyOptions imported from utils/controlConstants

    // Significance options
    const significanceOptions = ['', 'key', 'non-key'];

    // Control spec state (Stage 2)
    const [controlSpec, setControlSpec] = useState(null);
    const [editableSpec, setEditableSpec] = useState(null);

    // UI state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Initializing...');
    const [loadingDetail, setLoadingDetail] = useState(null);
    const [loadingIcon, setLoadingIcon] = useState('loader');
    const [showDetailedView, setShowDetailedView] = useState(false);
    const [isEditingSteps, setIsEditingSteps] = useState(false);

    // Check for cached data or navigation state on mount
    useEffect(() => {
        const extractionCached = sessionStorage.getItem(EXTRACTION_CACHE_KEY);
        const specCached = sessionStorage.getItem(CONTROL_SPEC_CACHE_KEY);

        // Priority 0: If draftControlId is passed (activating a draft control)
        // Fetch draft data from backend and go to CONFIRM_EXTRACTION stage (skip AI extraction)
        if (draftControlId) {
            const loadDraft = async () => {
                try {
                    const hash = userHash || localStorage.getItem('userHash');
                    const response = await axiosInstance.get(`/api/sox-controls/${draftControlId}`, {
                        headers: { 'Authorization': hash || '' }
                    });
                    const draft = response.data;
                    const draftInfo = {
                        control_id: draft.control_id || '',
                        control_title: draft.title || '',
                        control_wording_raw: draft.control_wording_text || '',
                        control_objective: draft.objective || '',
                        frequency: draft.frequency || '',
                        sox_process: draft.sox_process || '',
                        control_performer: draft.control_performer || '',
                        significance: draft.significance || ''
                    };
                    setExtractedInfo(draftInfo);
                    setEditableControlId(draftInfo.control_id);
                    setEditableTitle(draftInfo.control_title);
                    setEditableFrequency(draftInfo.frequency);
                    setEditableWording(draftInfo.control_wording_raw);
                    setEditableObjective(draftInfo.control_objective);
                    setEditableSoxProcess(draftInfo.sox_process);
                    setEditableControlPerformer(draftInfo.control_performer);
                    setEditableSignificance(draftInfo.significance);
                    setStage(STAGE.CONFIRM_EXTRACTION);
                    setLoading(false);
                } catch (err) {
                    console.error('Failed to load draft control:', err);
                    setError('Failed to load draft control. Redirecting...');
                    setTimeout(() => navigate('/sox-controls'), 2000);
                }
            };
            loadDraft();
            return;
        }

        // Priority 0.5: If extractedInfo is passed from unified upload (AI already extracted)
        // Skip extraction stage, go directly to CONFIRM_EXTRACTION with pre-filled fields
        if (stateExtractedInfo) {
            setExtractedInfo(stateExtractedInfo);
            setEditableControlId(stateExtractedInfo.control_id || '');
            setEditableTitle(stateExtractedInfo.control_title || '');
            setEditableFrequency(stateExtractedInfo.frequency || '');
            setEditableWording(stateExtractedInfo.control_wording_raw || '');
            setEditableObjective(stateExtractedInfo.control_objective || '');
            setEditableSoxProcess(stateExtractedInfo.sox_process || '');
            setEditableControlPerformer(stateExtractedInfo.control_performer || '');
            setEditableSignificance(stateExtractedInfo.significance || '');
            if (stateExtractedInfo.existing_test_steps && stateExtractedInfo.existing_test_steps.length > 0) {
                setExistingTestSteps(stateExtractedInfo.existing_test_steps);
            }

            // Cache extraction for back-navigation
            sessionStorage.setItem(EXTRACTION_CACHE_KEY, JSON.stringify({
                extractedInfo: stateExtractedInfo,
                fileName: file?.name,
                fileSize: file?.size,
                timestamp: Date.now()
            }));

            setStage(STAGE.CONFIRM_EXTRACTION);
            setLoading(false);
            return;
        }

        // Priority 1: If controlSpec is passed via navigation state (back from DataSourceConfiguration)
        // Go directly to REVIEW_STEPS stage
        if (stateControlSpec) {
            setControlSpec(stateControlSpec);
            setEditableSpec(stateControlSpec);
            setStage(STAGE.REVIEW_STEPS);
            setLoading(false);

            // Also restore extractedInfo from cache if available (needed for "Back" from REVIEW_STEPS)
            if (extractionCached) {
                try {
                    const parsedExtraction = JSON.parse(extractionCached);
                    if (parsedExtraction.extractedInfo) {
                        setExtractedInfo(parsedExtraction.extractedInfo);
                        setEditableControlId(parsedExtraction.extractedInfo.control_id || '');
                        setEditableTitle(parsedExtraction.extractedInfo.control_title || '');
                        setEditableFrequency(parsedExtraction.extractedInfo.frequency || '');
                        setEditableWording(parsedExtraction.extractedInfo.control_wording_raw || '');
                        setEditableObjective(parsedExtraction.extractedInfo.control_objective || '');
                        setEditableSoxProcess(parsedExtraction.extractedInfo.sox_process || '');
                        setEditableControlPerformer(parsedExtraction.extractedInfo.control_performer || '');
                        setEditableSignificance(parsedExtraction.extractedInfo.significance || '');
                    }
                } catch (e) {
                    console.error('Failed to parse cached extraction:', e);
                }
            }
            // If no cached extraction, reconstruct minimal extractedInfo from controlSpec
            if (!extractionCached) {
                const reconstructedInfo = {
                    control_id: stateControlSpec.control_id,
                    control_title: stateControlSpec.control_title,
                    control_wording_raw: stateControlSpec.control_wording_text || '',
                    control_objective: stateControlSpec.control_objective || '',
                    frequency: stateControlSpec.frequency,
                    sox_process: stateControlSpec.sox_process || '',
                    control_performer: stateControlSpec.control_performer || '',
                    significance: stateControlSpec.significance || ''
                };
                setExtractedInfo(reconstructedInfo);
                setEditableControlId(reconstructedInfo.control_id);
                setEditableTitle(reconstructedInfo.control_title);
                setEditableFrequency(reconstructedInfo.frequency);
                setEditableWording(reconstructedInfo.control_wording_raw);
                setEditableObjective(reconstructedInfo.control_objective);
                setEditableSoxProcess(reconstructedInfo.sox_process);
                setEditableControlPerformer(reconstructedInfo.control_performer);
                setEditableSignificance(reconstructedInfo.significance);
            }
            return;
        }

        // Priority 2: Check for cached control spec
        if (specCached) {
            try {
                const parsedSpec = JSON.parse(specCached);
                const isSameFile = file && parsedSpec.fileName === file.name && parsedSpec.fileSize === file.size;
                const isNavigatingBack = !file;

                if (parsedSpec.controlSpec && (isNavigatingBack || isSameFile)) {
                    setControlSpec(parsedSpec.controlSpec);
                    setEditableSpec(parsedSpec.controlSpec);
                    setStage(STAGE.REVIEW_STEPS);
                    setLoading(false);
                    return;
                }
            } catch (e) {
                console.error('Failed to parse cached control spec:', e);
            }
        }

        // Priority 3: Check for cached extraction (for back from REVIEW_STEPS to CONFIRM_EXTRACTION)
        if (extractionCached) {
            try {
                const parsedExtraction = JSON.parse(extractionCached);
                const isSameFile = file && parsedExtraction.fileName === file.name && parsedExtraction.fileSize === file.size;
                const isNavigatingBack = !file;

                if (parsedExtraction.extractedInfo && (isNavigatingBack || isSameFile)) {
                    setExtractedInfo(parsedExtraction.extractedInfo);
                    setEditableControlId(parsedExtraction.extractedInfo.control_id || '');
                    setEditableTitle(parsedExtraction.extractedInfo.control_title || '');
                    setEditableFrequency(parsedExtraction.extractedInfo.frequency || '');
                    setEditableWording(parsedExtraction.extractedInfo.control_wording_raw || '');
                    setEditableObjective(parsedExtraction.extractedInfo.control_objective || '');
                    setEditableSoxProcess(parsedExtraction.extractedInfo.sox_process || '');
                    setEditableControlPerformer(parsedExtraction.extractedInfo.control_performer || '');
                    setEditableSignificance(parsedExtraction.extractedInfo.significance || '');
                    setStage(STAGE.CONFIRM_EXTRACTION);
                    setLoading(false);
                    return;
                }
            } catch (e) {
                console.error('Failed to parse cached extraction:', e);
            }
        }

        // Priority 4: Clear old caches and start fresh extraction only if we have a new file
        if (file) {
            sessionStorage.removeItem(EXTRACTION_CACHE_KEY);
            sessionStorage.removeItem(CONTROL_SPEC_CACHE_KEY);
        }
    }, [file, stateControlSpec, draftControlId, stateExtractedInfo]);

    // The single-file extraction endpoint (/extract-info-stream) was removed. Extraction
    // now happens exclusively on /sox-controls/new via the /extract-stream endpoint;
    // that page navigates here with `extractedInfo` already in route state. If we land on
    // this route without it (stale tab, manual URL, etc.) we redirect back to the upload
    // page rather than silently re-extracting.
    useEffect(() => {
        const hasInputState = extractedInfo || controlSpec || stateControlSpec || draftControlId || stateExtractedInfo;
        if (!hasInputState) {
            setError('Missing extracted control. Please start from the upload page.');
            setTimeout(() => navigate('/sox-controls/new'), 2000);
        }
    }, [file, stage, loading, extractedInfo, controlSpec]);

    // Stage 2: Generate test steps with SSE streaming
    const generateTestSteps = async () => {
        // Validate required fields
        const missingFields = [];
        if (!editableControlId.trim()) missingFields.push('Control ID');
        if (!editableTitle.trim()) missingFields.push('Control Title');
        if (!editableFrequency.trim()) missingFields.push('Frequency');
        if (!editableWording.trim()) missingFields.push('Control Wording');
        if (!editableObjective.trim()) missingFields.push('Control Objective');
        if (!editableSoxProcess.trim()) missingFields.push('SOX Process');
        if (!editableControlPerformer.trim()) missingFields.push('Control Performer');
        if (!editableSignificance.trim()) missingFields.push('Significance');

        if (missingFields.length > 0) {
            setShowFieldHints(true);
            // Scroll to the first empty field smoothly
            return;
        }

        setShowFieldHints(false);

        setLoading(true);
        setError(null);
        setStage(STAGE.GENERATING_STEPS);
        setLoadingMessage('Preparing to generate test steps...');
        setLoadingDetail(null);
        setLoadingIcon('sparkles');

        try {
            const requestBody = {
                control_id: editableControlId,
                control_title: editableTitle,
                control_wording_raw: editableWording,
                control_objective: editableObjective,
                frequency: editableFrequency
            };

            // Use streaming endpoint via Go backend proxy
            const response = await axiosInstance.post('/api/controls/generate-test-steps-stream', requestBody, {
                adapter: 'fetch',
                responseType: 'stream',
                validateStatus: () => true
            });

            if (response.status === 403) {
                let message = 'An error occurred while generating test steps';
                try {
                    const text = await new Response(response.data).text();
                    const parsed = JSON.parse(text);
                    message = parsed.error || parsed.message || parsed.detail || message;
                } catch { /* keep default */ }
                throw new Error(message);
            }

            if (response.status < 200 || response.status >= 300) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Read SSE stream
            const reader = response.data.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEventType = 'status';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEventType = line.slice(7).trim();
                        continue;
                    }
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            // Handle error events
                            if (currentEventType === 'error') {
                                const errorMsg = data.message || 'An error occurred while generating test steps';
                                throw new Error(errorMsg);
                            }

                            // Handle status events
                            if (currentEventType === 'status') {
                                if (data.message) {
                                    setLoadingMessage(data.message);
                                }
                                if (data.detail) {
                                    setLoadingDetail(data.detail);
                                }
                                if (data.icon) {
                                    setLoadingIcon(data.icon);
                                }
                            }

                            // Handle result event (has test_steps array)
                            if (currentEventType === 'result' && data.test_steps) {
                                // Merge user's edited values with AI response
                                const mergedSpec = {
                                    ...data,
                                    control_id: editableControlId || data.control_id,
                                    control_title: editableTitle || data.control_title,
                                    control_objective: editableObjective || data.control_objective,
                                    control_wording_text: editableWording || data.control_wording_text,
                                    control_wording_raw: editableWording || data.control_wording_raw,
                                    frequency: editableFrequency || data.frequency
                                };
                                setControlSpec(mergedSpec);
                                setEditableSpec(mergedSpec);
                                initializeControlSpec(mergedSpec);

                                // Cache the control spec with user's edits
                                sessionStorage.setItem(CONTROL_SPEC_CACHE_KEY, JSON.stringify({
                                    controlSpec: mergedSpec,
                                    fileName: file?.name || null,
                                    fileSize: file?.size || null,
                                    timestamp: Date.now()
                                }));

                                setLoadingMessage('Test steps generated!');
                                setLoadingIcon('check');

                                setTimeout(() => {
                                    setStage(STAGE.REVIEW_STEPS);
                                    setLoading(false);
                                }, 500);
                            }
                        } catch (parseErr) {
                            // Re-throw non-JSON errors (like our error events)
                            if (parseErr.message && !parseErr.message.includes('JSON')) {
                                throw parseErr;
                            }
                        }
                    }
                }
            }

        } catch (err) {
            console.error('Error generating test steps:', err);
            setError(resolve403ErrorMessage(err, err.message || 'Failed to generate test steps'));
            setLoading(false);
            setStage(STAGE.CONFIRM_EXTRACTION);
        }
    };

    const handleBack = () => {
        if (stage === STAGE.REVIEW_STEPS) {
            // Go back to extraction confirmation
            setStage(STAGE.CONFIRM_EXTRACTION);
            setControlSpec(null);
            setEditableSpec(null);
            sessionStorage.removeItem(CONTROL_SPEC_CACHE_KEY);
        } else if (draftControlId) {
            // Coming from Configure on Controls list — go back there
            navigate('/sox-controls');
        } else {
            navigate('/sox-controls/new');
        }
    };

    const handleCancel = () => {
        sessionStorage.removeItem(EXTRACTION_CACHE_KEY);
        sessionStorage.removeItem(CONTROL_SPEC_CACHE_KEY);
        window.location.href = '/sox-controls';
    };

    const handleTestStepChange = (index, field, value) => {
        setEditableSpec(prev => {
            const newSteps = [...prev.test_steps];
            newSteps[index] = { ...newSteps[index], [field]: value };
            return { ...prev, test_steps: newSteps };
        });
    };

    const addTestStep = () => {
        setEditableSpec(prev => ({
            ...prev,
            test_steps: [...(prev.test_steps || []), {
                summary: 'New test step',
                description: '',
                expected_outcome: '',
                responsible_party: 'preparer',
                audit_procedure: 'inspection'
            }]
        }));
    };

    const removeTestStep = (index) => {
        setEditableSpec(prev => ({
            ...prev,
            test_steps: prev.test_steps.filter((_, i) => i !== index)
        }));
    };

    // --- Test Attributes CRUD ---------------------------------------------
    // Spec: specs/30-modules/skeptics-frontend/test-step-generation.spec.md

    const [attributesExpanded, setAttributesExpanded] = useState({});

    const toggleAttributesExpanded = (stepIdx) => {
        setAttributesExpanded(prev => ({ ...prev, [stepIdx]: !prev[stepIdx] }));
    };

    const requiredAttrCount = (step) =>
        (step.test_attributes || []).filter(a => a.required !== false).length;

    const handleAttributeChange = (stepIdx, attrIdx, field, value) => {
        setEditableSpec(prev => {
            const steps = [...prev.test_steps];
            const step = { ...steps[stepIdx] };
            const attrs = [...(step.test_attributes || [])];
            attrs[attrIdx] = { ...attrs[attrIdx], [field]: value };
            // Invariant: enum_values null when data_type leaves enum
            if (field === 'data_type' && value !== 'enum') {
                attrs[attrIdx].enum_values = null;
            }
            step.test_attributes = attrs;
            steps[stepIdx] = step;
            return { ...prev, test_steps: steps };
        });
    };

    const addAttribute = (stepIdx) => {
        setEditableSpec(prev => {
            const steps = [...prev.test_steps];
            const step = { ...steps[stepIdx] };
            const attrs = [...(step.test_attributes || [])];
            if (attrs.length >= 10) return prev; // Invariant I6
            attrs.push({
                key: '',
                label: '',
                description: '',
                data_type: 'text',
                expected_value: null,
                enum_values: null,
                required: true,
                source_hint: null,
            });
            step.test_attributes = attrs;
            steps[stepIdx] = step;
            return { ...prev, test_steps: steps };
        });
        setAttributesExpanded(prev => ({ ...prev, [stepIdx]: true }));
    };

    const removeAttribute = (stepIdx, attrIdx) => {
        setEditableSpec(prev => {
            const steps = [...prev.test_steps];
            const step = { ...steps[stepIdx] };
            step.test_attributes = (step.test_attributes || []).filter((_, i) => i !== attrIdx);
            steps[stepIdx] = step;
            return { ...prev, test_steps: steps };
        });
    };

    const handleUseExistingSteps = useCallback(() => {
        setEditableSpec(prev => ({
            ...prev,
            test_steps: existingTestSteps.map((step, idx) => ({
                ...step,
                step_number: idx + 1
            }))
        }));
    }, [existingTestSteps]);

    const [isSaving, setIsSaving] = useState(false);

    const handleNext = async () => {
        const finalSpec = {
            ...editableSpec,
            control_id: editableControlId || editableSpec?.control_id,
            control_title: editableTitle || editableSpec?.control_title,
            control_objective: editableObjective || editableSpec?.control_objective,
            control_wording_text: editableWording || editableSpec?.control_wording_text,
            control_wording_raw: editableWording || editableSpec?.control_wording_raw,
            frequency: editableFrequency || editableSpec?.frequency,
            sox_process: editableSoxProcess || editableSpec?.sox_process || '',
            control_performer: editableControlPerformer || editableSpec?.control_performer || '',
            significance: editableSignificance || editableSpec?.significance || ''
        };

        setIsSaving(true);
        try {
            const userHash = localStorage.getItem('userHash');
            const controlData = {
                control_id: finalSpec.control_id || 'UNKNOWN',
                title: finalSpec.control_title || 'Untitled Control',
                objective: finalSpec.control_objective || '',
                control_wording_text: finalSpec.control_wording_text || '',
                control_type: 'manual',
                frequency: finalSpec.frequency || '',
                test_steps: finalSpec.test_steps || [],
                data_source_type: '',
                data_source_config: {},
                control_upload_id: fileId ? parseInt(fileId, 10) : null,
                sox_process: finalSpec.sox_process || '',
                control_performer: finalSpec.control_performer || '',
                significance: finalSpec.significance || ''
            };

            let response;
            if (draftControlId) {
                controlData.status = 'active';
                response = await axiosInstance.put(`/api/sox-controls/${draftControlId}`, controlData, {
                    headers: { 'Authorization': userHash || '' }
                });
            } else {
                response = await axiosInstance.post('/api/sox-controls', controlData, {
                    headers: { 'Authorization': userHash || '' }
                });
            }

            navigate('/sox-controls/create-wizard/data-source', {
                state: {
                    controlSpec: finalSpec,
                    fileId,
                    file,
                    draftControlId,
                    skipToConfirmation: true,
                    savedControlData: {
                        title: controlData.title,
                        control_id: controlData.control_id,
                        control_type: controlData.control_type,
                        control_wording: controlData.control_wording_text,
                        objective: controlData.objective,
                        test_steps: controlData.test_steps,
                        filesCount: 0,
                        integrationsCount: 0,
                        sox_process: controlData.sox_process,
                        control_performer: controlData.control_performer,
                        significance: controlData.significance
                    }
                }
            });
        } catch (error) {
            console.error('Error saving control:', error);
            alert(resolve403ErrorMessage(error, 'Failed to save control. Please try again.'));
        }
        setIsSaving(false);
    };

    // Loading state — uses the same wizard shell as CONFIRM_EXTRACTION and
    // REVIEW_STEPS so the sidebar and top bar stay visible during extraction
    // and test step generation. Spinner card renders inside the main content
    // column, not as a full-screen takeover.
    if (loading) {
        const isExtracting = stage === STAGE.EXTRACTING;
        const isComplete = loadingIcon === 'check';

        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Top Bar — matches CONFIRM_EXTRACTION/REVIEW_STEPS */}
                <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBack}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title="Back"
                        >
                            <ArrowLeft className="h-5 w-5 text-gray-500" />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                            <p className="text-sm text-gray-500">
                                {isExtracting ? 'Extracting control information' : 'Generating test steps'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                {/* Main Content — spinner card lives inside the same main column */}
                <div className="flex-1 max-w-4xl mx-auto w-full px-8 py-12">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
                        <div className="flex flex-col items-center space-y-8">
                            {/* Animated Icon with Pulse Effect */}
                            <div className="relative">
                                {!isComplete && (
                                    <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping"></div>
                                )}

                                <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-500 ${
                                    isComplete
                                        ? 'bg-gradient-to-br from-green-400 to-green-600 scale-110'
                                        : 'bg-gradient-to-br from-blue-500 to-purple-600'
                                }`}>
                                    {!isComplete && <Loader2 className="w-10 h-10 text-white animate-spin" />}
                                    {isComplete && <Check className="w-10 h-10 text-white" />}
                                </div>

                                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                                    <Sparkles className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            <div className="text-center space-y-3">
                                <h3 className="text-xl font-bold text-gray-900">
                                    {isExtracting ? 'Extracting Control' : 'Generating Test Steps'}
                                </h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {isExtracting
                                        ? 'AI is analyzing your control document...'
                                        : 'AI is creating comprehensive test procedures...'}
                                </p>
                            </div>

                            <div className="w-full max-w-md bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
                                <p className="text-sm text-blue-900 font-medium text-center">
                                    {loadingMessage}
                                </p>
                                {loadingDetail && (
                                    <p className="text-xs text-blue-700 text-center mt-2 opacity-75">
                                        {loadingDetail}
                                    </p>
                                )}
                            </div>

                            {!isComplete && (
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-96 space-y-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <div className="text-center space-y-3 w-full max-w-md">
                    <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
                    <p className="text-sm text-gray-600">{error}</p>
                    <p className="text-xs text-gray-400">Please try again. If the problem persists, try uploading a different file format.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => {
                            setError(null);
                            if (stage === STAGE.GENERATING_STEPS) {
                                setStage(STAGE.CONFIRM_EXTRACTION);
                            }
                        }}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
                    >
                        Try Again
                    </button>
                    <button
                        onClick={() => navigate('/sox-controls/new')}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                    >
                        Upload New File
                    </button>
                </div>
            </div>
        );
    }

    // Stage 1: Confirm Extraction
    if (stage === STAGE.CONFIRM_EXTRACTION && extractedInfo) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Top Bar */}
                <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                            <ArrowLeft className="h-5 w-5 text-gray-500" />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                            <p className="text-sm text-gray-500">Review extracted control information</p>
                        </div>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 max-w-4xl mx-auto w-full px-8 py-12">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-8">
                            {/* Step Indicator */}
                            <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
                                <div className="flex items-center gap-2">
                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">Step 1 of 2</span>
                                </div>
                                <span className="text-sm text-gray-500">Confirm Control Information</span>
                            </div>

                            <div className="flex items-center gap-2 mb-1">
                                <Check className="w-5 h-5 text-green-600" />
                                <h2 className="text-lg font-semibold text-gray-900">Control Information Extracted</h2>
                            </div>
                            <p className="text-sm text-gray-500 mb-6">
                                Review the details below and edit if needed.
                            </p>

                            {/* Extracted Control Information */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                                <h3 className="text-sm font-semibold text-blue-900 mb-4">Extracted Control Information</h3>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs text-blue-600 font-medium mb-1">Control ID</label>
                                            <input
                                                type="text"
                                                value={editableControlId}
                                                onChange={(e) => setEditableControlId(e.target.value)}
                                                className="w-full bg-white border border-blue-300 rounded px-3 py-2 text-sm text-blue-900 font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter control ID..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-blue-600 font-medium mb-1">Frequency</label>
                                            <select
                                                value={editableFrequency}
                                                onChange={(e) => setEditableFrequency(e.target.value)}
                                                className="w-full bg-white border border-blue-300 rounded px-3 py-2 text-sm text-blue-900 capitalize focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                {frequencyOptions.map(freq => (
                                                    <option key={freq} value={freq} className="capitalize">{freq}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs text-blue-600 font-medium mb-1">Control Title</label>
                                        <input
                                            type="text"
                                            value={editableTitle}
                                            onChange={(e) => setEditableTitle(e.target.value)}
                                            className="w-full bg-white border border-blue-300 rounded px-3 py-2 text-sm text-blue-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter control title..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-blue-600 font-medium mb-1">Control Wording</label>
                                        <AutoResizeTextarea
                                            value={editableWording}
                                            onChange={(e) => setEditableWording(e.target.value)}
                                            className="w-full bg-white border border-blue-300 rounded px-3 py-2 text-sm text-blue-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter control wording..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-blue-600 font-medium mb-1">Control Objective (AI-Interpreted)</label>
                                        <AutoResizeTextarea
                                            value={editableObjective}
                                            onChange={(e) => setEditableObjective(e.target.value)}
                                            className="w-full bg-white border border-blue-300 rounded px-3 py-2 text-sm text-blue-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter control objective..."
                                        />
                                    </div>

                                    {/* Additional Control Details - Integrated naturally */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="flex items-center gap-1 text-xs text-blue-600 font-medium mb-1">
                                                SOX Process
                                                {!editableSoxProcess.trim() && (
                                                    <AlertTriangle className="h-3 w-3 text-red-500" />
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                value={editableSoxProcess}
                                                onChange={(e) => setEditableSoxProcess(e.target.value)}
                                                className={`w-full bg-white border rounded px-3 py-2 text-sm text-blue-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                                                    !editableSoxProcess.trim() 
                                                        ? 'border-red-400'
                                                        : 'border-blue-300'
                                                }`}
                                                placeholder="e.g., Revenue, Procurement..."
                                            />
                                        </div>
                                        <div>
                                            <label className="flex items-center gap-1 text-xs text-blue-600 font-medium mb-1">
                                                Control Performer
                                                {!editableControlPerformer.trim() && (
                                                    <AlertTriangle className="h-3 w-3 text-red-500" />
                                                )}
                                            </label>
                                            <input
                                                type="text"
                                                value={editableControlPerformer}
                                                onChange={(e) => setEditableControlPerformer(e.target.value)}
                                                className={`w-full bg-white border rounded px-3 py-2 text-sm text-blue-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                                                    !editableControlPerformer.trim() 
                                                        ? 'border-red-400'
                                                        : 'border-blue-300'
                                                }`}
                                                placeholder="e.g., Accounting Manager..."
                                            />
                                        </div>
                                        <div>
                                            <label className="flex items-center gap-1 text-xs text-blue-600 font-medium mb-1">
                                                Significance
                                                {!editableSignificance.trim() && (
                                                    <AlertTriangle className="h-3 w-3 text-red-500" />
                                                )}
                                            </label>
                                            <select
                                                value={editableSignificance}
                                                onChange={(e) => setEditableSignificance(e.target.value)}
                                                className={`w-full bg-white border rounded px-3 py-2 text-sm text-blue-900 capitalize focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                                                    !editableSignificance.trim() 
                                                        ? 'border-red-400'
                                                        : 'border-blue-300'
                                                }`}
                                            >
                                                <option value="">Select significance...</option>
                                                <option value="key">Key</option>
                                                <option value="non-key">Non-Key</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-between items-center pt-4">
                                <button
                                    onClick={handleBack}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back
                                </button>
                                <button
                                    onClick={generateTestSteps}
                                    disabled={!editableControlId.trim() || !editableTitle.trim() || !editableFrequency.trim() || !editableWording.trim() || !editableObjective.trim() || !editableSoxProcess.trim() || !editableControlPerformer.trim() || !editableSignificance.trim()}
                                    className={`inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                                        (!editableControlId.trim() || !editableTitle.trim() || !editableFrequency.trim() || !editableWording.trim() || !editableObjective.trim() || !editableSoxProcess.trim() || !editableControlPerformer.trim() || !editableSignificance.trim())
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Confirm & Generate Test Steps
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Stage 2: Review Test Steps
    if (stage === STAGE.REVIEW_STEPS && editableSpec) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Top Bar */}
                <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                            <ArrowLeft className="h-5 w-5 text-gray-500" />
                        </button>
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Control Creation Wizard</h1>
                            <p className="text-sm text-gray-500">Review AI-generated test steps</p>
                        </div>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 max-w-4xl mx-auto w-full px-8 py-12">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-8">
                            {/* Step Indicator */}
                            <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-6">
                                <div className="flex items-center gap-2">
                                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">Step 2 of 2</span>
                                </div>
                                <span className="text-sm text-gray-500">Review Test Steps</span>
                            </div>

                            <div className="flex items-center justify-between mb-1">
                                <h2 className="text-lg font-semibold text-gray-900">Review Control & Test Steps</h2>
                            </div>
                            <p className="text-sm text-gray-500 mb-6">
                                Review the AI-generated test steps. You can edit them before proceeding.
                            </p>

                            {/* Control Summary (Read-only recap) */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-4">
                                {/* Basic Info Row */}
                                <div className="flex items-center gap-6 text-sm">
                                    <div>
                                        <span className="text-gray-500">ID:</span>
                                        <span className="ml-1 font-semibold text-gray-900">{editableSpec.control_id}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Frequency:</span>
                                        <span className="ml-1 text-gray-900 capitalize">{editableSpec.frequency}</span>
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-gray-500">Title:</span>
                                        <span className="ml-1 text-gray-900">{editableSpec.control_title}</span>
                                    </div>
                                </div>

                                {/* Control Wording */}
                                {(editableSpec.control_wording_text || editableWording) && (
                                    <div className="border-t border-gray-200 pt-3">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Control Wording</span>
                                        <p className="mt-1 text-sm text-gray-700 leading-relaxed">{editableSpec.control_wording_text || editableWording}</p>
                                    </div>
                                )}

                                {/* Control Objective */}
                                {editableSpec.control_objective && (
                                    <div className="border-t border-gray-200 pt-3">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Control Objective <span className="text-blue-600 font-normal">(AI-Interpreted)</span></span>
                                        <p className="mt-1 text-sm text-gray-700 leading-relaxed">{editableSpec.control_objective}</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                {/* SCRUM-268: Existing Test Steps from Document */}
                                {existingTestSteps && existingTestSteps.length > 0 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-3.5 h-3.5 text-amber-600" />
                                                <h3 className="text-sm font-semibold text-gray-900">Existing Test Steps from Document</h3>
                                                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{existingTestSteps.length} steps found</span>
                                            </div>
                                            <button
                                                onClick={handleUseExistingSteps}
                                                className="text-xs font-medium text-amber-700 bg-amber-100 border border-amber-300 px-3 py-1 rounded-md hover:bg-amber-200 transition-colors"
                                            >
                                                Use These Instead
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            {existingTestSteps.map((step, idx) => (
                                                <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded border border-amber-100">
                                                    <span className="flex-shrink-0 w-5 h-5 bg-amber-200 text-amber-800 text-xs font-medium rounded-full flex items-center justify-center mt-0.5">
                                                        {idx + 1}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-gray-700">{step.summary || step.description?.substring(0, 120)}</p>
                                                        {(step.responsible_party || step.audit_procedure) && (
                                                            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                                                                {step.responsible_party && <span>👤 {step.responsible_party}</span>}
                                                                {step.audit_procedure && <span>🔍 {step.audit_procedure}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-amber-600 mt-2">
                                            Compare with the AI-generated steps below. You can use these, the AI steps, or edit either set.
                                        </p>
                                    </div>
                                )}

                                {/* Test Steps Header */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                                        <h3 className="text-sm font-semibold text-gray-900">AI-Generated Test Steps</h3>
                                        <span className="text-xs text-gray-500">({editableSpec?.test_steps?.length || 0})</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isEditingSteps && (
                                            <button
                                                onClick={addTestStep}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                            >
                                                <Plus className="w-3 h-3" />
                                                Add Step
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setIsEditingSteps(!isEditingSteps)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                                isEditingSteps
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Edit2 className="w-3 h-3" />
                                            {isEditingSteps ? 'Done Editing' : 'Edit'}
                                        </button>
                                        <button
                                            onClick={() => setShowDetailedView(!showDetailedView)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                        >
                                            {showDetailedView ? (
                                                <>
                                                    <EyeOff className="w-3 h-3" />
                                                    Simplified View
                                                </>
                                            ) : (
                                                <>
                                                    <Eye className="w-3 h-3" />
                                                    Detailed View
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Test Steps List */}
                                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                                    {editableSpec?.test_steps?.map((step, index) => (
                                        <div key={index} className="p-4">
                                            {!showDetailedView ? (
                                                <div className="flex items-start gap-3">
                                                    <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white text-xs font-medium rounded-full flex items-center justify-center mt-1">
                                                        {index + 1}
                                                    </span>
                                                    {isEditingSteps ? (
                                                        <AutoResizeTextarea
                                                            value={step.summary || ''}
                                                            onChange={(e) => handleTestStepChange(index, 'summary', e.target.value)}
                                                            className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    ) : (
                                                        <p className="flex-1 text-sm text-gray-700 leading-relaxed">{step.summary}</p>
                                                    )}
                                                    {isEditingSteps && (
                                                        <button
                                                            onClick={() => removeTestStep(index)}
                                                            className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors mt-1"
                                                            title="Delete step"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="flex items-start gap-3">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white text-xs font-medium rounded-full flex items-center justify-center">
                                                            {index + 1}
                                                        </span>
                                                        <div className="flex-1">
                                                            {isEditingSteps ? (
                                                                <input
                                                                    type="text"
                                                                    value={step.summary || ''}
                                                                    onChange={(e) => handleTestStepChange(index, 'summary', e.target.value)}
                                                                    className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                />
                                                            ) : (
                                                                <p className="text-sm font-medium text-gray-900">{step.summary}</p>
                                                            )}
                                                        </div>
                                                        {isEditingSteps && (
                                                            <button
                                                                onClick={() => removeTestStep(index)}
                                                                className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                                                title="Delete step"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="ml-9 space-y-3">
                                                        <div>
                                                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</span>
                                                            {isEditingSteps ? (
                                                                <AutoResizeTextarea
                                                                    value={step.description || ''}
                                                                    onChange={(e) => handleTestStepChange(index, 'description', e.target.value)}
                                                                    className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                />
                                                            ) : (
                                                                <p className="mt-1 text-sm text-gray-600 leading-relaxed">{step.description}</p>
                                                            )}
                                                        </div>

                                                        {step.expected_outcome && (
                                                            <div>
                                                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Outcome</span>
                                                                {isEditingSteps ? (
                                                                    <AutoResizeTextarea
                                                                        value={step.expected_outcome || ''}
                                                                        onChange={(e) => handleTestStepChange(index, 'expected_outcome', e.target.value)}
                                                                        className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                    />
                                                                ) : (
                                                                    <p className="mt-1 text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">{step.expected_outcome}</p>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="flex flex-wrap gap-2 pt-1">
                                                            {step.responsible_party && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 capitalize">
                                                                    {step.responsible_party}
                                                                </span>
                                                            )}
                                                            {step.audit_procedure && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 capitalize">
                                                                    {step.audit_procedure}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Test Attributes panel — visible in BOTH simplified and detailed views.
                                                Spec: specs/30-modules/skeptics-frontend/test-step-generation.spec.md */}
                                            <div className="mt-3 ml-9 border-t border-slate-200 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleAttributesExpanded(index)}
                                                    className="flex items-center justify-between w-full text-sm font-medium text-slate-700 hover:text-slate-900"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        {attributesExpanded[index]
                                                            ? <ChevronDown className="w-4 h-4" />
                                                            : <ChevronRight className="w-4 h-4" />}
                                                        Test Attributes ({(step.test_attributes || []).length}
                                                        {requiredAttrCount(step) > 0 && ` · ${requiredAttrCount(step)} required`})
                                                    </span>
                                                    {isEditingSteps && (
                                                        <span
                                                            role="button"
                                                            onClick={(e) => { e.stopPropagation(); addAttribute(index); }}
                                                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                                                        >
                                                            <Plus className="w-3 h-3" /> Add Attribute
                                                        </span>
                                                    )}
                                                </button>
                                                {attributesExpanded[index] && (
                                                    <div className="mt-2 space-y-2">
                                                        {(step.test_attributes || []).map((attr, attrIdx) => (
                                                            <AttributeRow
                                                                key={attrIdx}
                                                                attr={attr}
                                                                isEditing={isEditingSteps}
                                                                onChange={(field, value) => handleAttributeChange(index, attrIdx, field, value)}
                                                                onDelete={() => removeAttribute(index, attrIdx)}
                                                            />
                                                        ))}
                                                        {(step.test_attributes || []).length === 0 && (
                                                            <p className="text-xs text-slate-500 italic">
                                                                No attributes defined — analyzer will use legacy extraction.
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Navigation Buttons */}
                                <div className="flex justify-between items-center pt-6">
                                    <button
                                        onClick={handleBack}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        disabled={isSaving}
                                        className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        {isSaving ? 'Saving...' : 'Save Control'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Fallback loading
    return (
        <div className="flex flex-col items-center justify-center h-96 space-y-6">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            <div className="text-center space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Loading...</h3>
            </div>
        </div>
    );
};

export default TestStepGeneration;
